/**
 * Реферальная программа.
 *
 * НАБРОСОК. Суммы, имена и списки ниже вымышлены: в API сейчас есть только
 * сам реферальный код — ни статистики, ни начислений, ни выводов. Экран
 * нужен, чтобы согласовать раскладку до того, как писать под неё бэкенд.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useProfile } from '@/entities/subscription/queries';
import { config } from '@/shared/config/env';
import { shareLink } from '@/shared/lib/links';
import { useBackButton } from '@/shared/lib/useBackButton';
import { Screen } from '@/shared/ui/Screen';
import { Button } from '@/shared/ui/kit/Button';
import { Card, GroupLink } from '@/shared/ui/kit/Card';
import { Hero } from '@/shared/ui/kit/Hero';
import { Icon } from '@/shared/ui/kit/Icon';
import { Row } from '@/shared/ui/kit/Row';
import { Sheet } from '@/shared/ui/kit/Sheet';
import { Stat, StatGrid } from '@/shared/ui/kit/Stat';

import styles from './ReferralPage.module.css';

/** Сколько строк показывать на самой странице; остальное — в шторке. */
const PREVIEW = 3;
/** Сколько последних операций показывает шторка. */
const RECENT = 10;

/** Вымышленные данные. Удалить вместе с появлением API. */
const DRAFT = {
  invited: 7,
  earned: '1 340 ₽',
  available: '740 ₽',
  spend: [
    { id: 's1', name: 'Алексей', what: 'VPN, 3 месяца', reward: '+54 ₽' },
    { id: 's2', name: 'Марина', what: 'VPN, 1 год', reward: '+180 ₽' },
    { id: 's3', name: 'Игорь', what: 'VPN, 1 месяц', reward: '+20 ₽' },
    { id: 's4', name: 'Дмитрий', what: 'VPN, 6 месяцев', reward: '+100 ₽' },
    { id: 's5', name: 'Алексей', what: 'VPN, 1 месяц', reward: '+20 ₽' },
    { id: 's6', name: 'Ольга', what: 'VPN, 3 месяца', reward: '+54 ₽' },
    { id: 's7', name: 'Сергей', what: 'VPN, 1 год', reward: '+180 ₽' },
    { id: 's8', name: 'Марина', what: 'VPN, 1 месяц', reward: '+20 ₽' },
    { id: 's9', name: 'Никита', what: 'VPN, 6 месяцев', reward: '+100 ₽' },
    { id: 's10', name: 'Павел', what: 'VPN, 3 месяца', reward: '+54 ₽' },
    { id: 's11', name: 'Игорь', what: 'VPN, 1 месяц', reward: '+20 ₽' },
    { id: 's12', name: 'Ольга', what: 'VPN, 1 год', reward: '+180 ₽' },
  ],
  payouts: [
    { id: 'p1', when: '12 сентября', how: 'На подписку', amount: '600 ₽' },
    { id: 'p2', when: '3 августа', how: 'На подписку', amount: '400 ₽' },
    { id: 'p3', when: '19 июля', how: 'На подписку', amount: '200 ₽' },
    { id: 'p4', when: '2 июля', how: 'На подписку', amount: '140 ₽' },
  ],
};

function botLink(code: string): string {
  return `https://t.me/${config.botUsername}?start=ref_${code}`;
}

const SHARE_TEXT = 'Подключайся к ANROYAL VPN по моей ссылке';

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    };

    // Запасной путь нужен: clipboard недоступен вне защищённого контекста
    // и в части встроенных браузеров.
    const fallback = () => {
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.append(field);
      field.select();
      document.execCommand('copy');
      field.remove();
      done();
    };

    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(value).then(done, fallback);
      return;
    }
    fallback();
  };

  return (
    <Button variant="primary" block onClick={copy}>
      <Icon name={copied ? 'check' : 'copy'} size={18} />
      {copied ? 'Скопировано' : 'Скопировать ссылку'}
    </Button>
  );
}

function SpendRows({ limit }: { limit: number }) {
  return (
    <Card>
      {DRAFT.spend.slice(0, limit).map((item) => (
        <Row
          key={item.id}
          icon="users"
          title={item.name}
          subtitle={item.what}
          value={<span className={styles.money}>{item.reward}</span>}
        />
      ))}
    </Card>
  );
}

function PayoutRows({ limit }: { limit: number }) {
  if (DRAFT.payouts.length === 0) {
    return (
      <Card>
        <p className={styles.empty}>Выводов пока не было</p>
      </Card>
    );
  }
  return (
    <Card>
      {DRAFT.payouts.slice(0, limit).map((item) => (
        <Row key={item.id} icon="arrowUp" title={item.how} subtitle={item.when} value={item.amount} />
      ))}
    </Card>
  );
}

type OpenSheet = 'spend' | 'payouts' | null;

export function ReferralPage() {
  const navigate = useNavigate();
  const [sheet, setSheet] = useState<OpenSheet>(null);

  // Системная кнопка «назад» сначала закрывает шторку и только потом
  // уводит с экрана: иначе одно нажатие делает сразу два шага.
  useBackButton(() => {
    if (sheet !== null) {
      setSheet(null);
      return;
    }
    // Сюда приходят и из профиля, и с главной: возвращаемся туда, откуда пришли.
    void navigate(-1);
  });

  const { data, isLoading, error, refetch } = useProfile();
  const link = data?.referral_code ? botLink(data.referral_code) : '';

  return (
    <Screen isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      <Hero status={<span>Заработано</span>} value={DRAFT.earned} until="за всё время" />

      <Card padded>
        <div className={styles.link}>{link || '—'}</div>
        <div className={styles.copyRow}>
          <CopyButton value={link} />
          <Button variant="quiet" onClick={() => void shareLink(link, SHARE_TEXT)} aria-label="Поделиться">
            <Icon name="share" size={18} />
          </Button>
        </div>
      </Card>

      <StatGrid>
        <Stat icon="users" value={DRAFT.invited} label="Приглашено" />
        {/* Сумма уже вынесена в карточку-герой: повторять её в плитке
            нельзя, два одинаковых числа рядом читаются как разные. */}
        <Stat icon="wallet" value={DRAFT.available} label="Доступно к выводу" />
      </StatGrid>

      <GroupLink onClick={() => setSheet('spend')}>Расходы рефералов</GroupLink>
      <SpendRows limit={PREVIEW} />

      <GroupLink onClick={() => setSheet('payouts')}>История выводов</GroupLink>
      <PayoutRows limit={PREVIEW} />

      <Sheet
        open={sheet === 'spend'}
        title="Расходы рефералов"
        onClose={() => setSheet(null)}
      >
        <SpendRows limit={RECENT} />
      </Sheet>

      <Sheet
        open={sheet === 'payouts'}
        title="История выводов"
        onClose={() => setSheet(null)}
      >
        <PayoutRows limit={RECENT} />
      </Sheet>
    </Screen>
  );
}
